/**
 * Formats an HTTP error for display on the Simulation & Testing Page.
 *
 * Fixes DCP-51297. Two related problems with Angular's default rendering:
 *
 * 1. `HttpErrorResponse.message` is `"Http failure response for <url>:
 *    <status> OK"` even for non-OK statuses, because `statusText` defaults
 *    to `"OK"` when the response has no reason phrase (very common — HTTP/2
 *    responses have no reason phrase at all, per RFC 7540 §8.1.2.4).
 * 2. When Angular's XHR backend cannot read a `statusText` it substitutes
 *    the literal `"Unknown Error"`, producing confusing output like
 *    `HTTP 404 Unknown Error`.
 *
 * This helper produces a more accurate message by:
 * - Reading the backend business message from the response body
 *   (`error.error.message`, or the first entry of an `errors[]` array as
 *   used by `dcp-lambda-pdp-decorator`, or a string body).
 * - Reading a semantic label from the response body's `type` field first
 *   (server-provided, always meaningful, e.g. `"NotFound"`,
 *   `"IllegalArgument"`, `"Unauthorized"`) — this is the canonical shape
 *   used by every DCP lambda's error DTO.
 * - Falling back to `statusText` only when it is not one of the Angular /
 *   fetch-API defaults that carry no diagnostic value.
 *
 * @param error The error object from an RxJS `error` callback (typically an
 *              Angular `HttpErrorResponse`, but tolerant of other shapes).
 * @returns Human-readable message suitable for `GlobalMessageService.add`.
 */
/**
 * Values that Angular / the fetch API fall back to when the server does not
 * populate the reason phrase. They mislead the reader ("HTTP 500 OK",
 * "HTTP 404 Unknown Error") and should be suppressed. Common sources:
 * - "OK"            — XMLHttpRequest default for any status, incl. 4xx/5xx
 * - "Unknown Error" — Angular HttpXhrBackend default when statusText is empty
 *                     (frequent with HTTP/2 responses, which have no reason
 *                     phrase per RFC 7540 §8.1.2.4)
 * - ""              — fetch API when the server did not set a reason phrase
 * - "undefined"/"null" (as strings) — defensive
 *
 * Declared at module scope so we do not reallocate the Set on every call.
 */
const IGNORED_STATUS_TEXTS = new Set(['', 'OK', 'Unknown Error', 'undefined', 'null']);

export function formatTestPageError(error: unknown): string {
  const err = error as {
    status?: number;
    statusText?: string;
    message?: string;
    error?:
      | string
      | {
          type?: string;
          message?: string;
          errors?: Array<{type?: string; message?: string; errorCode?: string}>;
        };
  };

  // Prefer backend business message. Response body can be:
  // - object with `.message` (typical error DTO)
  // - object with `.errors[]` array (dcp-lambda-pdp-decorator shape)
  // - a plain string
  let businessMessage = '';
  let bodyType = '';
  if (typeof err?.error === 'string') {
    businessMessage = err.error;
  } else if (err?.error && typeof err.error === 'object') {
    businessMessage = err.error.message ?? err.error.errors?.[0]?.message ?? '';
    bodyType = err.error.type ?? err.error.errors?.[0]?.type ?? '';
  }

  // Build HTTP context using the actual numeric status. Use `!= null`
  // (not truthy) so that `status: 0` (network / CORS / client-side error)
  // is still rendered as `HTTP 0`.
  //
  // For the qualifier after the status code, prefer the server-provided
  // `type` field from the response body (semantic, e.g. `"NotFound"`),
  // then fall back to `statusText` if it is not one of the known-misleading
  // defaults captured in IGNORED_STATUS_TEXTS.
  const status = err?.status;
  const rawStatusText = (err?.statusText ?? '').trim();
  const usableStatusText = rawStatusText && !IGNORED_STATUS_TEXTS.has(rawStatusText) ? rawStatusText : '';
  const qualifier = bodyType || usableStatusText;

  let httpContext: string;
  if (status != null) {
    const qualifierSuffix = qualifier ? ' ' + qualifier : '';
    httpContext = 'HTTP ' + status + qualifierSuffix;
  } else {
    httpContext = err?.message ?? 'Unknown error';
  }

  return businessMessage ? businessMessage + ' (' + httpContext + ')' : httpContext;
}
